import { SectionHeader } from "@/components/site/SectionHeader";
import { ContactBanner } from "@/components/site/ContactBanner";
import { Separator } from "@/components/ui/separator";
import { Globe, Briefcase, Shield, Users } from "lucide-react";
import {
  FadeIn, FadeInX, StaggerContainer, StaggerItem,
  RevealLine, ImageReveal, PageTransition, GoldBar,
} from "@/components/site/Animations";

const PORTRAIT_URL = "https://customer-assets.emergentagent.com/job_cortes-execution/artifacts/2ru3rsfx_image.png";

const CAREER_HIGHLIGHTS = [
  {
    title: "Cortes Industries",
    role: "Founder, Chief Executive Officer & Principal",
    location: "Salt Lake City, Utah",
    points: [
      "Founder and executive leader of a procurement and contracting firm supporting mission-critical programs and cross-agency initiatives",
      "Focused on strategic sourcing, capture support, and alignment across government entities, prime contractors, and commercial partners",
      "Led end-to-end procurement and contracting workstreams supporting complex program delivery and operational requirements",
      "Supported capture and pursuit strategy including teaming, positioning, and compliance inputs for government and prime contractor engagements",
      "Served as interface across agencies, primes, and vendors \u2014 aligning stakeholders and accelerating procurement and contracting decisions",
    ],
  },
  {
    title: "Warrant One (formerly Opulence Capital)",
    role: "Co-Managing Director | Government Relations & Public Affairs",
    location: "Doha, Qatar",
    summary: "Co-lead of an American-owned advisory and liaison firm operating across the MENA/Near East region, structuring and de-risking cross-border defense and energy initiatives at the intersection of US policy and Gulf capital.",
    points: [
      "Drive US engagement strategy for international business and technology initiatives, translating complex regulatory environments into actionable pathways for partners and stakeholders",
      "Build and steward high-trust relationships with policymakers, regulators, and industry principals to advance strategic partnerships and mandates",
      "Serve as principal on US domestic risk, geopolitical developments, and shifting policy dynamics affecting international operations, delivering forward-looking assessments that shape deal posture and timing",
      "Architect and execute communications strategy while coordinating cross-functional development efforts end-to-end",
      "Maintain rigorous OFAC and FARA compliance across all engagements, embedding regulatory discipline into every stage of deal structuring",
    ],
  },
  {
    title: "E. Khashoggi Industries (formerly Triad Group)",
    role: "Research Analyst | Client Relations Manager | Board Observer",
    location: "Goleta, California",
    points: [
      "Conducted strategic market intelligence and technology research supporting international investment initiatives",
      "Analyzed emerging markets in advanced materials, renewable energy, water technologies, and sustainable packaging",
      "Produced analytical reports and financial models evaluating venture investments, licensing opportunities, and strategic technologies",
      "Managed stakeholder communications and presented research findings to executives and board advisors",
    ],
  },
  {
    title: "Pacific West Resources",
    role: "Co-Founder & Principal",
    location: "Santiago, Chile",
    points: [
      "International mining and resource development executive with 10+ years of experience acquiring and developing strategic mineral assets across emerging markets",
      "Specializing in precious metals and critical materials supporting global industrial supply chains",
      "Led cross-border concession negotiations, resource acquisitions, and joint ventures with host governments and private partners",
      "Directed feasibility analysis, strategic resource assessments, and regulatory coordination for international development programs",
      "Managed contractors and technical teams while producing operational and technical reports for investors and strategic partners",
    ],
  },
  {
    title: "Broadway Capital",
    role: "Co-Founder & Principal",
    location: "Salt Lake City, Utah",
    points: [
      "Led international expansion of fabricated construction systems and building materials supporting cross-border development initiatives and global construction supply networks",
      "Directed market expansion of prefabricated housing systems and construction materials across multiple Asian and Middle Eastern markets",
      "Coordinated analysts, quality assurance teams, and project managers to implement scalable operational frameworks",
      "Supported sourcing strategies, logistics coordination, and partnerships related to modular construction technologies and building materials",
    ],
  },
];

const EXPERTISE_AREAS = [
  "Business Model Optimization", "Project & Innovation Management", "Strategy Deployment",
  "Critical & Analytical Troubleshooting", "Rigorous Research Methods", "Global Strategic Alliances",
  "Key Partnership Development", "Tactical Market Planning & Risk Assessment", "Public & Media Relations",
];

export default function LeadershipPage() {
  return (
    <PageTransition>
      <div data-testid="leadership-page">
        <section className="bg-white pt-10 sm:pt-12 lg:pt-16 pb-10">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <FadeIn>
              <SectionHeader
                eyebrow="Our Team"
                title="Leadership"
                description="Cortes Industries is led by experienced operators with decades of domestic and international experience in procurement, supply chain management, and mission-critical program execution."
              />
            </FadeIn>
          </div>
        </section>

        <RevealLine />

        <section className="py-14 sm:py-16 lg:py-20 bg-white" data-testid="leadership-executive-bio">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-4">
                <div className="sticky top-24">
                  <ImageReveal>
                    <div className="exec-portrait aspect-[3/4] max-w-[360px] mb-5 img-zoom" data-testid="ceo-portrait-frame">
                      <img src={PORTRAIT_URL} alt="Doug Quezada" className="w-full h-full object-cover" />
                    </div>
                  </ImageReveal>
                  <FadeIn delay={0.3}>
                    <h2 className="text-2xl font-semibold tracking-tight text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                      Douglas P. Quezada
                    </h2>
                    <p className="text-sm text-[hsl(41,47%,61%)] font-medium mt-1" style={{ fontFamily: 'var(--font-display)' }}>Chief Executive Officer</p>
                    <p className="text-xs text-muted-foreground mt-2">Political Science & Legal Studies — Brigham Young University</p>
                    <p className="text-xs text-muted-foreground mt-1">Languages: English, Spanish</p>
                  </FadeIn>
                </div>
              </div>

              <div className="lg:col-span-8">
                <FadeIn delay={0.15}>
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold tracking-tight text-foreground mb-4" style={{ fontFamily: 'var(--font-display)' }}>Executive Biography</h3>
                    <div className="space-y-4 text-sm sm:text-base leading-relaxed text-muted-foreground">
                      <p>Doug Quezada is an accomplished, agile executive with over twenty years of domestic and international experience in operations, P&L oversight, multichannel product distribution, and marketing across both startup and growth organizations.</p>
                      <p>A results-driven leader, Quezada has a proven track record in new market identification and strategic positioning for high-stakes technology and commodity sector organizations. His experience spans increasing sales, growing bottom lines, and spearheading operational improvements to drive productivity and reduce inefficiencies.</p>
                      <p>Doug Quezada and associates have negotiated cross-border supply and technology agreements and directed teams through high-stakes procurement and contracting cycles. Cortes Industries and its partner network operate across the United States, Latin America, Central and Eastern Europe, the Middle East and North Africa, and East and Southeast Asia.</p>
                      <p>Quezada and global partners excel in dynamic, demanding environments — remaining pragmatic and focused on goal execution and target deliverables. The team brings experience in navigating complex political landscapes, government engagement, and stakeholder coordination.</p>
                    </div>
                  </div>
                </FadeIn>

                <Separator className="my-8" />

                <FadeIn delay={0.2}>
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold tracking-tight text-foreground mb-6" style={{ fontFamily: 'var(--font-display)' }}>Career Highlights</h3>
                    <div className="space-y-8">
                      {CAREER_HIGHLIGHTS.map((role, i) => (
                        <FadeIn key={role.title} delay={i * 0.08}>
                          <div className="border-l-2 border-[hsl(41,47%,61%)] pl-5">
                            <h4 className="text-base font-semibold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>{role.title}</h4>
                            <p className="text-sm text-[hsl(41,47%,61%)] font-medium mt-0.5">{role.role}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{role.location}</p>
                            {role.summary && (
                              <p className="mt-3 text-sm leading-relaxed text-foreground/80" data-testid={`career-summary-${i}`}>{role.summary}</p>
                            )}
                            <ul className="mt-3 space-y-1.5">
                              {role.points.map((point) => (
                                <li key={point} className="flex items-start gap-2">
                                  <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 shrink-0" />
                                  <p className="text-sm text-muted-foreground">{point}</p>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </FadeIn>
                      ))}
                    </div>
                  </div>
                </FadeIn>

                <Separator className="my-8" />

                <FadeIn delay={0.1}>
                  <div>
                    <h3 className="text-lg font-semibold tracking-tight text-foreground mb-4" style={{ fontFamily: 'var(--font-display)' }}>Areas of Expertise</h3>
                    <StaggerContainer stagger={0.04} className="flex flex-wrap gap-2">
                      {EXPERTISE_AREAS.map((area) => (
                        <StaggerItem key={area}>
                          <span className="inline-flex px-3 py-1.5 text-xs font-medium bg-secondary text-foreground rounded-md border border-border badge-glow">{area}</span>
                        </StaggerItem>
                      ))}
                    </StaggerContainer>
                  </div>
                </FadeIn>
              </div>
            </div>
          </div>
        </section>

        <RevealLine />

        {/* Partner Network */}
        <section className="py-14 sm:py-16 lg:py-20 bg-secondary/40">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <FadeIn>
              <SectionHeader
                eyebrow="Network"
                title="Global Partner Network"
                description="Cortes Industries operates through a network of associates and specialists with complementary expertise across regions and sectors."
                align="center"
                className="mb-10"
              />
            </FadeIn>
            <StaggerContainer stagger={0.1} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-4xl mx-auto auto-rows-fr">
              {[
                { icon: Globe, title: "Global Reach", desc: "U.S., Latin America, CEE, MENA, ESEA" },
                { icon: Briefcase, title: "Operator-Led", desc: "Hands-on execution with P&L accountability" },
                { icon: Shield, title: "Risk-First", desc: "Structured risk assessment for adverse conditions" },
                { icon: Users, title: "IP-Aware", desc: "Protecting intellectual property in complex partnerships" },
              ].map((item) => (
                <StaggerItem key={item.title} className="flex">
                  <div className="text-center p-6 bg-white border border-border rounded-lg card-hover flex-1 flex flex-col items-center">
                    <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center mx-auto mb-3">
                      <item.icon className="w-5 h-5 text-[hsl(41,47%,61%)]" />
                    </div>
                    <h4 className="text-sm font-semibold text-foreground mb-1" style={{ fontFamily: 'var(--font-display)' }}>{item.title}</h4>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        <ContactBanner />
      </div>
    </PageTransition>
  );
}
